import { saveAs } from 'file-saver';

export const generateError = ({ status, statusText }) =>
	new Error(`Middleware error: ${status} - ${statusText}`);

export async function getVehicles() {
	const response = await this('/vehicle/tree');

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getVendors() {
	const response = await this('/fitment/vendors');

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getCategoryAndGroupFilters() {
	const response = await this('/oms/product-filters');

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getProductTypes() {
	const response = await this('/oms/products/filters/types');

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getProductNamesAndColors(reqParams) {
	const params = new URLSearchParams(reqParams);
	const response = await this(
		`/oms/products/filters/names?${params.toString()}`,
	);

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getProductDetails(reqParams) {
	const params = new URLSearchParams(reqParams);
	const response = await this(`/oms/products?${params.toString()}`);

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getProductsBySku(skuList) {
	const response = await this(`/oms/productBySku?skuList=${skuList}`);

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getCustomers(keyword, options = {}) {
	const params = new URLSearchParams();
	params.set('search_keywords', keyword);
	const response = await this(
		`/oms/customer-search?${params.toString()}`,
		options,
	);

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getOrders(filters = {}) {
	const parameters = new URLSearchParams(
		Object.entries(filters)
			// Format special parameters
			.map(([key, value]) => {
				switch (key) {
					case 'photos':
						return [key, value === true ? '1' : '0'];
					case 'tags':
						return [
							key,
							(Array.isArray(value) ? value : [value])
								.filter((tags) => tags.length > 0)
								.toString(),
						];
					case 'year':
					case 'make':
					case 'model':
						return [key, value];
					default:
						return [key, value];
				}
			})
			// Don't include empty parameters
			.filter(([, value]) => value.length || !isNaN(value)),
	);

	const response = await this(`/oms/orders?${parameters}`);

	if (response.status === 200) return response.json();
	else if (response.status === 204) return undefined;

	throw generateError(response);
}

export async function getOrderById(id, next = false, prev = false) {
	const params = new URLSearchParams();

	if (next) params.set('next', '1');
	if (prev) params.set('prev', '1');

	const response = await this(`/oms/order/${id}?${params.toString()}`);

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function updateOrder(id, order, custom) {
	const response = await this(`/oms/order/${id}`, {
		method: 'PUT',
		body: JSON.stringify({ order, custom }),
	});

	if (response.status === 200) return response.json();
	else if (response.status === 204) return undefined;

	throw generateError(response);
}

export async function updateOrderStatus(orderIds, status) {
	const response = await this(
		`/oms/orders/statuses?ids=${orderIds.join(',')}`,
		{
			method: 'PUT',
			body: JSON.stringify({
				new_status_id: status,
			}),
		},
	);

	if (response.status === 200) return true;
	else if (response.status === 204) return undefined;

	throw generateError(response);
}

export async function updateOrderTags(ids, action, tag) {
	const response = await this('/oms/orders/tags', {
		method: 'PUT',
		body: JSON.stringify({ ids, action, tag }),
	});

	if (response.status === 200) return true;
	else if (response.status === 204) return undefined;

	throw generateError(response);
}

export async function getOrderStatuses() {
	const response = await this('/oms/order_statuses');

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getCustomerAddresses(customerId) {
	const response = await this(
		`/oms/customer-addresses?customer_id=${customerId}`,
	);

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function createOrder(orderBody) {
	const response = await this('/oms/checkout', {
		method: 'POST',
		body: JSON.stringify(orderBody),
	});

	if (!response.ok) throw generateError(response);
	if (response.status === 204) return { status: 204 };

	return response.json();
}

export async function getPrices(body) {
	const response = await this('/oms/product-prices', {
		method: 'POST',
		body: JSON.stringify(body),
	});

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function importFitment(file, onProgress) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.upload.addEventListener('progress', (event) =>
			onProgress(
				`Uploading Fitment Data...${Math.floor(
					(event.loaded / event.total) * 100,
				)}%`,
			),
		);
		xhr.addEventListener('error', reject);
		xhr.addEventListener('abort', reject);
		xhr.addEventListener('progress', (event) =>
			onProgress(event.target.responseText.split('\n').pop()),
		);
		xhr.addEventListener('load', () => {
			if (xhr.readyState !== xhr.DONE) return;

			switch (xhr.status) {
				case 200:
					return resolve(true);
				case 400:
					return resolve(false);
				default:
					return reject(
						generateError({
							status: xhr.status,
							statusText: xhr.statusText,
						}),
					);
			}
		});
		xhr.open(
			'POST',
			new URL('/fitment/import', this.base).toString(),
			true,
		);

		for (const header in this.headers)
			xhr.setRequestHeader(header, this.headers[header]);

		xhr.setRequestHeader('Content-Type', 'application/octet-stream');

		try {
			xhr.send(file);
		} catch (error) {
			reject(error);
		}
	});
}

export async function exportFitment(isTemplate, filters = {}) {
	const parameters = new URLSearchParams();

	Object.keys(filters).forEach((filter) => {
		if (filters[filter].length) parameters.set(filter, filters[filter]);
	});

	const response = await this(
		isTemplate
			? `/fitment/export/template`
			: `/fitment/export?${parameters.toString()}`,
	);

	if (!response.ok) throw generateError(response);

	// TODO: This shouldn't be here, it should be in the component logic
	Object.assign(document.createElement('a'), {
		target: '_blank',
		href: URL.createObjectURL(await response.blob()),
		download: isTemplate ? 'template.csv' : 'Fitment.csv',
	}).click();
}

export async function downloadOrders(orderIds) {
	const response = await this(
		`/oms/orders/download?ids=${orderIds.join(',')}`,
	);

	if (!response.ok) throw generateError(response);

	saveAs(await response.blob(), 'Orders.zip');
}

export async function exportOrders(exportType, orders, options = {}) {
	const { vendor, columns } = options;
	const payload = exportType === 'custom-csv'
		? { orderIds: orders, columns }
		: { orders, vendor };

	const response = await this(`/oms/export/${exportType}`, {
		method: 'POST',
		body: JSON.stringify(payload),
	});

	if (!response.ok) throw generateError(response);

	const data = await response.text();

	// Only handle automatic downloads for csv and pdf types
	if (exportType === 'custom-csv') {
		// Return the data for custom handling
		return data;
	}
	// TODO: This shouldn't be here, it should be in component logic
	else if (exportType === 'csv') {
		Object.assign(document.createElement('a'), {
			target: '_blank',
			href: `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`,
			download: 'Orders.csv',
		}).click();
	} else if (exportType === 'pdf') {
		Object.assign(document.createElement('a'), {
			target: '_blank',
			href: `data:application/pdf;base64,${data}`,
			download: 'Orders.pdf',
		}).click();
	}
}

export async function startCustomCsvExport(payload) {
	const response = await this(`/oms/export/custom-csv`, {
		method: 'POST',
		body: JSON.stringify(payload),
	});

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function checkCustomCsvExportStatus(jobId) {
	const response = await this(`/oms/export/custom-csv/${jobId}`);
	if (!response.ok) throw generateError(response);
	return response.json();
}

export async function getVehicleData() {
	try {
		const response = await this('/fitment/vehicle-data');
		
		if (!response.ok) {
			console.error('Vehicle data fetch failed:', response.status, response.statusText);
			throw generateError(response);
		}
		
		return response.json();
	} catch (error) {
		console.error('Vehicle data fetch error:', error);
		// Return default empty data instead of throwing to avoid breaking the UI
		return { years: [], makes: [], models: [] };
	}
}

export async function getFilteredMakes(year) {
	try {
		if (!year) {
			console.warn('getFilteredMakes called without a year parameter');
			return { makes: [] };
		}
		
		const response = await this(`/fitment/makes?year=${encodeURIComponent(year)}`);
		
		if (!response.ok) {
			console.error('Filtered makes fetch failed:', response.status, response.statusText);
			throw generateError(response);
		}
		
		return response.json();
	} catch (error) {
		console.error('Filtered makes fetch error:', error);
		// Return default empty data instead of throwing to avoid breaking the UI
		return { makes: [] };
	}
}

export async function getFilteredModels(year, make) {
	try {
		if (!year || !make) {
			console.warn('getFilteredModels called without year or make parameter');
			return { models: [] };
		}
		
		const response = await this(`/fitment/models?year=${encodeURIComponent(year)}&make=${encodeURIComponent(make)}`);
		
		if (!response.ok) {
			console.error('Filtered models fetch failed:', response.status, response.statusText);
			throw generateError(response);
		}
		
		return response.json();
	} catch (error) {
		console.error('Filtered models fetch error:', error);
		// Return default empty data instead of throwing to avoid breaking the UI
		return { models: [] };
	}
}

export async function getRecentCsvExportJobs() {
	const response = await this('/oms/export/jobs');

	if (!response.ok) throw generateError(response);

	return response.json();
}

export async function getOrderCountForDateRange(startDate, endDate) {
	const response = await this(`/oms/export/order-count?startDate=${startDate}&endDate=${endDate}`);

	if (!response.ok) throw generateError(response);

	return response.json();
}
